import { useMemo, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Button } from 'src/components/Button';
import { LoginMembership } from 'src/orchd-client';

const POPUP_NAME = 'github-app-registration';

interface Props {
  member: LoginMembership;
  onClose: () => void;
  buttonVariant?: 'primary' | 'secondary';
  buttonSize?: 'small';
}

export function GitHubAppManifestForm({ member, onClose, buttonVariant, buttonSize }: Props) {
  const formRef = useRef<HTMLFormElement>(null);
  const [error, setError] = useState<string | null>(null);

  const state = 'dummy-state-token';

  // Generate a unique app name using "Enhance" + formatted timestamp
  // This ensures each installation gets a unique name, avoiding clashes when apps are uninstalled and reinstalled
  const appName = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.toLocaleString('en-US', { month: 'short' });
    const day = now.getDate().toString().padStart(2, '0');
    const hours = now.getHours().toString().padStart(2, '0');
    const minutes = now.getMinutes().toString().padStart(2, '0');
    return `Enhance-${month}-${day}-${year}-${hours}:${minutes}`;
  }, []);

  const manifest = useMemo(
    () => ({
      name: appName,
      url: 'https://deploy.example.com',

      redirect_url: `https://${window.location.host}/api/orgs/${member.orgId}/projects/manifest-callback`,

      setup_url: `https://${window.location.host}/api/orgs/${member.orgId}/projects/install-complete`,

      hook_attributes: {
        url: `https://${window.location.host}/api/orgs/${member.orgId}/projects/webhook`,
        active: true,
      },

      public: true,

      default_permissions: {
        contents: 'read',
      },

      default_events: ['push'],
    }),
    [member.orgId, appName]
  );

  function openPopupAndSubmit() {
    setError(null);

    // Must be opened directly from the user's click to avoid
    // being treated as an unsolicited popup.
    const popup = window.open(
      '',
      POPUP_NAME,
      ['popup=yes', 'width=900', 'height=750', 'scrollbars=yes', 'resizable=yes'].join(',')
    );

    if (!popup) {
      setError('The popup was blocked by your browser.');
      return;
    }

    popup.focus();

    const form = formRef.current;

    if (!form) {
      popup.close();
      setError('The registration form is unavailable.');
      return;
    }

    // The form remains rendered inline, but GitHub's response
    // will load inside the named popup.
    form.requestSubmit();

    const interval = window.setInterval(() => {
      if (popup.closed) {
        clearInterval(interval);
        console.log('closed');
        onClose();
      }
    }, 500);
  }

  return (
    <form
      ref={formRef}
      method="post"
      target={POPUP_NAME}
      action={`https://github.com/settings/apps/new?state=${encodeURIComponent(state)}`}
    >
      <input type="hidden" name="manifest" value={JSON.stringify(manifest)} />

      <Button onClick={openPopupAndSubmit} variant={buttonVariant} size={buttonSize}>
        <FormattedMessage
          id="git.projects.connect_button"
          defaultMessage="Connect {github}"
          values={{ github: 'GitHub' }}
        />
      </Button>

      {error && <p role="alert">{error}</p>}
    </form>
  );
}
